import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import { cn } from "@/app/_utils/cn";
import { useCan } from "@/app/_utils/sessionClient";
import { PERMISSIONS } from "@/app/_utils/permissions";
import type { Role } from "@/app/_services/RbacService";
import { MoreHorizontal, Pencil, ShieldCheck, Trash2 } from "lucide-react";

export default function RoleList({
  roles,
  onEdit,
  onDelete,
}: {
  roles: Role[];
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null);
  const canWrite = useCan(PERMISSIONS.RBAC_ROLES_WRITE);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[760px] table-fixed", roles.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[28%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Role
            </TableHead>
            <TableHead className="h-8 w-[38%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Description
            </TableHead>
            <TableHead className="h-8 w-[16%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Permissions
            </TableHead>
            <TableHead className="h-8 w-[14%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Users
            </TableHead>
            <TableHead className="h-8 w-12 pr-6 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {roles.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={5} className="h-full p-0">
                <CuoEmpty icon={ShieldCheck} text="No roles" />
              </TableCell>
            </TableRow>
          ) : (
            roles.map((role) => (
              <TableRow
                key={role.id}
                className="group cursor-pointer hover:bg-muted/50"
                onClick={() => onEdit(role)}
              >
                <TableCell className="py-1.5">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium group-hover:underline">
                      {role.name}
                    </span>
                    {role.is_system && (
                      <Badge
                        variant="outline"
                        className="rounded-[7px] font-normal text-muted-foreground"
                      >
                        System
                      </Badge>
                    )}
                  </span>
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {role.description || "No description"}
                </TableCell>
                <TableCell className="py-1.5 text-[12.5px]">
                  {role.permissions.length}
                </TableCell>
                <TableCell className="py-1.5 text-[12.5px]">
                  {role.user_count}
                </TableCell>
                <TableCell
                  className="w-12 py-1.5 pr-6 text-right"
                  onClick={(event) => event.stopPropagation()}
                >
                  {canWrite && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                        >
                          <MoreHorizontal className="size-4 shrink-0" size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(role)}>
                          <Pencil className="mr-2 size-4" />
                          Edit
                        </DropdownMenuItem>
                        {/* System roles and roles still in use are rejected server-side. */}
                        {!role.is_system && role.user_count === 0 && (
                          <DropdownMenuItem
                            className="text-red-600 focus:bg-red-50 focus:text-red-700"
                            onClick={() => setPendingDelete(role)}
                          >
                            <Trash2 className="mr-2 size-4" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <CuoConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete role"
        message={`${pendingDelete?.name} will be removed. This cannot be undone.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
